import React, { useMemo, useState } from 'react';

interface IRepo<E> {
	allAsList: Array<E>
	byId(id: number) : E | undefined;
	etag: string
}

export interface IEntityStateAgg<E extends {id: number}, R extends IRepo<E> = IRepo<E>> {
	/** An entity or undefined. */
	value: E | undefined;
	/** A positive number for an entity, otherwise 0 (when undefined / absent). */
	id: number;
	/** A setter accepting an id, the entity itself or undefined. */
	setter(idOrEntity: number | E | undefined) : void;
	/** A handler for e.g., inputs. */
	changeHandler(event: React.ChangeEvent<{value: number|string}>): void
	/** Usually an IEntityStore. */
	repo: R;
}

export function useEntityStateAgg<E extends {id: number}, R extends IRepo<E> = IRepo<E>>(repo: R, initial?: E | undefined) : IEntityStateAgg<E, R> {
	const [value, setValue] = useState(initial);
	function setter(idOrEntity: number|E|undefined) {
		setValue(typeof idOrEntity !== 'number' ? idOrEntity : repo.byId(idOrEntity));
	}
	function changeHandler(event: React.ChangeEvent<{value: number|string}>): void {
		setValue(repo.byId(+event.target.value));
	}
	const id = value?.id ?? 0;
	return useMemo(() => ({id, value, setter, changeHandler, repo}), [repo, value, repo.etag]);
}
